import React from 'react';
import { useLatestValueReferenciallyStable } from './useMakeMutable.js';
/**
 * This is a custom hook that allows us to simulate a synthetic event. This ensure we only trigger a side effect when a specific variable(dependency) changes.
 * @param {any} dependency - the variable that we want to listen to
 * @param {Function} callback - the callback that we want to execute when the dependency changes
 * @param {object} callbackOpts - the options to be passed to the callback function
 * @returns {void}
 * @invoke useLatestValueReferenciallyStable
 */
export const useSideEffectSyntheticEvent = <T, CBOpts>(
  dependency: T,
  callback: (latestVal: T, opts: CBOpts) => void,
  callbackOpts: CBOpts,
) => {
  const refCb = useLatestValueReferenciallyStable(callback);
  const refCbOpts = useLatestValueReferenciallyStable(callbackOpts);
  // this is the useEffect that simulates the event
  React.useEffect(() => {
    refCb?.current?.(dependency, refCbOpts.current);
    // the point of this custom hook is making sure we only listen to the dependency and nothing else
    //  without violating the rules of hooks/exhaustive-deps rule and making it explicit that we want to simulate an event
    //  refCb & refCbOpts are forced to be referentially stable, so we can safely put it in the dependency array
  }, [dependency, refCb, refCbOpts]);
};
